"use client";

import { memo, useEffect, useMemo, useRef } from "react";
import {
  motion,
  useScroll,
  useVelocity,
  useMotionValue,
  useAnimationFrame,
  type MotionValue,
} from "framer-motion";
import { PortableText } from "next-sanity";
import { PortableTextBlock } from "sanity";
import { CTAButton } from "@/shadcn/ui/button";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { GROQButton } from "@/sanity/lib/definitions";
import { PageHomeLivePayoutItem } from "@/sanity/pages/PageHomeTemplate";
import TrustpilotRating from "@/components/pageComponents/global/TrustpilotRating";

interface ILivePayoutsSectionData {
  livePayoutsChip?: string;
  livePayoutsTitle?: PortableTextBlock[];
  livePayoutsButton?: GROQButton;
  livePayoutsPartnerText?: string;
  livePayoutsItems?: PageHomeLivePayoutItem[];
  trustpilotRating?: string;
}

const REPEATS = 4;
const BASE_PX_PER_FRAME = 1.5;
const GAP_PX = 24;

function IQLogo() {
  return (
    <svg
      viewBox="0 0 31.0799 25.9511"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="h-[3.25rem] w-[3.875rem] shrink-0"
      aria-hidden="true"
    >
      <path
        d="M0 24.2671V0.787903H5.73838V24.2671H0Z"
        fill="white"
        fillOpacity="0.15"
      />
      <path
        d="M25.5823 25.9511L23.8006 23.7691C23.319 24.0106 22.7091 24.1958 21.9868 24.3327C21.2645 24.4615 20.4619 24.534 19.5791 24.534C17.2516 24.534 15.2131 24.0187 13.4795 22.9961C11.746 21.9735 10.4057 20.5322 9.45864 18.6803C8.5116 16.8284 8.03809 14.7027 8.03809 12.2952C8.03809 9.88768 8.4795 7.86667 9.3543 6.01474C10.2291 4.16281 11.5373 2.69737 13.2628 1.61842C14.9884 0.539475 17.0911 0 19.5791 0C22.067 0 24.1617 0.539475 25.8712 1.61842C27.5887 2.69737 28.8809 4.16281 29.7637 6.01474C30.6385 7.86667 31.0799 9.96015 31.0799 12.2952C31.0799 14.4772 30.7187 16.3694 29.9884 17.9637C29.2661 19.566 28.2388 20.9107 26.9226 22.0138L30.0847 25.9511H25.5662H25.5823ZM19.5871 19.8317C19.8279 19.8317 20.0526 19.8156 20.2452 19.7834C20.4458 19.7512 20.6304 19.7029 20.807 19.6304L17.6128 15.7253H22.1312L23.5839 17.6416C24.0895 17.0941 24.4828 16.3936 24.7717 15.5401C25.0606 14.6946 25.1971 13.6076 25.1971 12.2791C25.1971 10.9505 25.0044 9.68639 24.6192 8.54302C24.234 7.39966 23.632 6.46564 22.8054 5.74903C21.9787 5.03241 20.9113 4.67813 19.5951 4.67813C18.2789 4.67813 17.1713 5.04046 16.3447 5.74903C15.518 6.46564 14.9161 7.39966 14.5309 8.54302C14.1457 9.68639 13.953 10.9344 13.953 12.2791C13.953 13.6237 14.1537 14.8396 14.5469 15.9829C14.9402 17.1263 15.5501 18.0523 16.3768 18.7608C17.2034 19.4694 18.2709 19.8156 19.5871 19.8156V19.8317Z"
        fill="white"
        fillOpacity="0.15"
      />
    </svg>
  );
}

const PayoutCard = memo(function PayoutCard({
  item,
}: {
  item: PageHomeLivePayoutItem;
}) {
  return (
    <article className="relative flex h-[11.5rem] w-[39.75rem] shrink-0 items-center overflow-hidden rounded-[0.25rem] border-l-2 border-[#1aa87f] bg-white/[0.07]">
      {/* IQ logo */}
      <div className="absolute left-[3.375rem] top-1/2 -translate-y-1/2">
        <IQLogo />
      </div>

      {/* Vertical divider */}
      <div className="absolute left-[9.75rem] top-1/2 h-[6.5rem] w-px -translate-y-1/2 bg-white/10" />

      {/* Text content */}
      <div className="absolute left-[13rem] top-[2.5rem] flex flex-col gap-[0.875rem]">
        <div className="flex items-center gap-[1.25rem] whitespace-nowrap">
          {item.name && (
            <p className="mb-0 font-geist text-[1.75rem] font-medium leading-[1.2] tracking-[-0.07rem] text-white/60">
              {item.name}
            </p>
          )}
          {item.amount && (
            <p className="mb-0 font-geist text-[2.5rem] font-semibold leading-[1.2] tracking-[-0.1rem] text-white">
              {item.amount}
            </p>
          )}
        </div>
        {item.country && (
          <div className="inline-flex w-fit items-center bg-[rgba(26,77,63,0.86)] px-[0.875rem] py-[0.5rem]">
            <p className="mb-0 whitespace-nowrap font-geist-mono text-[1.5rem] font-medium uppercase leading-[1.2] tracking-[-0.06rem] text-white">
              {item.country}
            </p>
          </div>
        )}
      </div>
    </article>
  );
});

function MarqueeRow({
  items,
  direction,
  scrollVelocity,
}: {
  items: PageHomeLivePayoutItem[];
  direction: 1 | -1;
  scrollVelocity: MotionValue<number>;
}) {
  const x = useMotionValue(0);
  const firstCopyRef = useRef<HTMLDivElement>(null);
  const singleWidthRef = useRef(0);
  const initialized = useRef(false);
  const isPaused = useRef(false);

  const repeated = useMemo(
    () => Array.from({ length: REPEATS }, () => items),
    [items],
  );

  useEffect(() => {
    const update = () => {
      if (firstCopyRef.current) {
        singleWidthRef.current = firstCopyRef.current.offsetWidth + GAP_PX;
      }
    };
    update();
    const ro = new ResizeObserver(update);
    if (firstCopyRef.current) ro.observe(firstCopyRef.current);
    return () => ro.disconnect();
  }, []);

  useAnimationFrame((_, delta) => {
    if (isPaused.current) return;
    const singleWidth = singleWidthRef.current;
    if (singleWidth <= GAP_PX) return;

    if (!initialized.current) {
      initialized.current = true;
      if (direction === 1) {
        x.set(-singleWidth);
        return;
      }
    }

    const vel = scrollVelocity.get();
    const boost = Math.min(Math.abs(vel) * 0.003, 6);
    const speed = BASE_PX_PER_FRAME * (1 + boost) * (delta / 16.67);

    if (direction === -1) {
      let newX = x.get() - speed;
      if (newX < -singleWidth) newX += singleWidth;
      x.set(newX);
    } else {
      let newX = x.get() + speed;
      if (newX > 0) newX -= singleWidth;
      x.set(newX);
    }
  });

  return (
    <div
      className="relative w-full overflow-hidden"
      style={{
        maskImage:
          "linear-gradient(to right, transparent 0%, black 12%, black 88%, transparent 100%)",
        WebkitMaskImage:
          "linear-gradient(to right, transparent 0%, black 12%, black 88%, transparent 100%)",
      }}
      onMouseEnter={() => {
        isPaused.current = true;
      }}
      onMouseLeave={() => {
        isPaused.current = false;
      }}
    >
      <motion.div
        style={{ x }}
        className="flex gap-[3rem] will-change-transform"
      >
        {repeated.map((copyItems, r) => (
          <div
            key={r}
            ref={r === 0 ? firstCopyRef : undefined}
            className="flex gap-[3rem] shrink-0"
          >
            {copyItems.map((item, i) => (
              <PayoutCard key={`${r}-${i}-${item._key ?? ""}`} item={item} />
            ))}
          </div>
        ))}
      </motion.div>
    </div>
  );
}

function TrustpilotStars({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      width="122"
      height="23"
      fill="none"
      viewBox="0 0 122 23"
    >
      <g clipPath="url(#clip_ts_stories)">
        <path
          fill="#219653"
          d="M22.875 0H0v22.875h22.875zM47.656 0H24.781v22.875h22.875zM72.438 0H49.561v22.875h22.876zM97.219 0H74.344v22.875h22.875zM122 0H99.125v22.875H122z"
        />
        <path
          fill="#fff"
          d="m11.437 15.417 3.48-.882 1.453 4.48zm8.007-5.79H13.32L11.437 3.86 9.555 9.626H3.43l4.956 3.575-1.882 5.766 4.956-3.574 3.05-2.192zM36.219 15.417l3.478-.882 1.454 4.48zm8.006-5.79H38.1L36.218 3.86l-1.882 5.766h-6.124l4.957 3.575-1.883 5.766 4.956-3.574 3.05-2.192zM61 15.417l3.479-.882 1.453 4.48zm8.006-5.79h-6.124L61 3.86l-1.883 5.766h-6.124l4.957 3.575-1.883 5.766 4.956-3.574 3.05-2.192zM85.78 15.417l3.48-.882 1.453 4.48zm8.007-5.79h-6.124L85.781 3.86l-1.883 5.766h-6.123l4.956 3.575-1.882 5.766 4.956-3.574 3.05-2.192zM110.562 15.417l3.479-.882 1.454 4.48zm8.006-5.79h-6.123l-1.883-5.767-1.882 5.766h-6.124l4.956 3.575-1.882 5.766 4.956-3.574 3.05-2.192z"
        />
      </g>
      <defs>
        <clipPath id="clip_ts_stories">
          <path fill="#fff" d="M0 0h122v22.875H0z" />
        </clipPath>
      </defs>
    </svg>
  );
}

function FooterBar({ data }: { data: ILivePayoutsSectionData }) {
  if (!data.livePayoutsButton && !data.livePayoutsPartnerText) return null;
  return (
    <div className="w-full bg-white/10">
      <div className="mx-auto flex w-[min(140rem,calc(100%-4rem))] flex-col items-center justify-between gap-[3rem] py-[6.875rem] lg:w-[min(140rem,calc(100%-8rem))] lg:flex-row">
        <div className="flex shrink-0 items-center justify-between gap-[1.875rem]">
          {/* <TrustpilotRating
            rating={data.trustpilotRating}
            showLabel={false}
            textColor="white"
            className="w-[12.5rem] h-[2.34rem] shrink-0"
          /> */}
          {data.livePayoutsPartnerText && (
            <p className="mb-0  lg:max-w-[40rem] font-geist text-[1.75rem] leading-[1.5] text-white/70 text-center lg:text-left">
              {data.livePayoutsPartnerText}
            </p>
          )}
        </div>
        {data.livePayoutsButton && (
          <div className="w-full shrink-0 lg:w-fit">
            <CTAButton data={data.livePayoutsButton} />
          </div>
        )}
      </div>
    </div>
  );
}

export default function LivePayoutsSection({
  data,
  hideCta,
}: {
  data: ILivePayoutsSectionData;
  hideCta?: boolean;
}) {
  const { scrollY } = useScroll();
  const scrollVelocity = useVelocity(scrollY);

  const items = data.livePayoutsItems ?? [];
  if (!data.livePayoutsTitle && !items.length) return null;

  return (
    <section className="relative w-full overflow-x-clip bg-[#001b14] lg:pt-0 pt-[10rem]">
      {/* Header */}
      <div className="mx-auto mb-[8rem] flex w-[min(90rem,calc(100%-4rem))] flex-col items-center gap-[3rem] px-[2rem] text-center lg:px-0">
        {data.livePayoutsChip && (
          <p className="mb-0 font-blacker italic text-[2rem] leading-[1.5] text-white/70">
            {data.livePayoutsChip}
          </p>
        )}
        {data.livePayoutsTitle && (
          <div className="richtext prose-headings:text-white [&_h1]:[text-wrap:balance] [&_h2]:[text-wrap:balance]">
            <PortableText
              value={data.livePayoutsTitle}
              components={richTextComponent}
            />
          </div>
        )}
        {!hideCta && data.livePayoutsButton && (
          <CTAButton data={data.livePayoutsButton} />
        )}
      </div>

      {/* 3 marquee rows — each row gets a unique slice of items so no name repeats across rows */}
      {items.length > 0 &&
        (() => {
          const chunk = Math.ceil(items.length / 3);
          const row1 = items.slice(0, chunk);
          const row2 = items.slice(chunk, chunk * 2);
          const row3 = items.slice(chunk * 2);
          return (
            <div className="flex flex-col gap-[3rem] pb-[8rem] lg:pb-[16rem]">
              <MarqueeRow
                items={row1}
                direction={-1}
                scrollVelocity={scrollVelocity}
              />
              <MarqueeRow
                items={row2}
                direction={1}
                scrollVelocity={scrollVelocity}
              />
              <MarqueeRow
                items={row3}
                direction={-1}
                scrollVelocity={scrollVelocity}
              />
            </div>
          );
        })()}

      <FooterBar data={data} />
    </section>
  );
}
